import "./boardingform.css";
import React, { useState } from "react";

const encode = (data) =>
  Object.keys(data)
    .map((key) => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
    .join("&");

export default function DogBoardingIntakeForm() {
  const [state, setState] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setState((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    fetch("/", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: encode({ "form-name": "dog-boarding-intake", ...state }),
    })
      .then(() => setSubmitted(true))
      .catch((err) => alert(err));
  };

  if (submitted) {
    return (
      <div className="intake-wrap">
        <div className="intake-card fade-in-5">
          <div className="header-1">
            <a href="/" className="home-button-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                height="24px"
                viewBox="0 -960 960 960"
                width="24px"
                fill="#1f1f1f"
              >
                <path d="M400-80 0-480l400-400 71 71-329 329 329 329-71 71Z" />
              </svg>
            </a>
            <h1 className="header">Return Home</h1>
          </div>
          <h2 className="intake-title">
            Thanks! We received your intake form.
          </h2>
          <p className="intake-text">
            We’ll review it and follow up if we have any questions.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="header-1">
        <a href="/" className="home-button">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            height="24px"
            viewBox="0 -960 960 960"
            width="24px"
            fill="#1f1f1f"
          >
            <path d="M400-80 0-480l400-400 71 71-329 329 329 329-71 71Z" />
          </svg>
        </a>
        <h1 className="header">Boarding Intake Form</h1>
      </div>
      <div className="intake-wrap">
        <form
          className="intake-form"
          name="dog-boarding-intake"
          method="POST"
          data-netlify="true"
          data-netlify-honeypot="bot-field"
          onSubmit={handleSubmit}
        >
          {/* Netlify required hidden input */}
          <input type="hidden" name="form-name" value="dog-boarding-intake" />

          {/* Honeypot */}
          <p className="sr-only">
            <label>
              Don’t fill this out:{" "}
              <input name="bot-field" onChange={handleChange} />
            </label>
          </p>

          <header className="intake-header">
            <p className="intake-subtitle fade-in-5">
              Thank you for trusting us with your dog. Please complete this form
              before your dog’s stay so we can provide safe, consistent care.
            </p>
          </header>

          <Section title="Dog Information">
            <div className="intake-grid fade-in-5">
              <Field
                label="Dog’s Name"
                name="dogName"
                onChange={handleChange}
                required
              />
              <Field label="Breed" name="breed" onChange={handleChange} />
              <Field label="Age" name="age" onChange={handleChange} />
              <Field
                label="Weight"
                name="weight"
                onChange={handleChange}
                placeholder="lbs or kg"
              />
            </div>
          </Section>

          <Section title="Feeding Schedule">
            <div className="intake-grid fade-in-5">
              <Field
                label="Breakfast (time + amount)"
                name="breakfast"
                onChange={handleChange}
                placeholder="e.g., 8:00 AM - 1 cup"
              />
              <Field
                label="Dinner (time + amount)"
                name="dinner"
                onChange={handleChange}
                placeholder="e.g., 6:00 PM - 1 cup"
              />
            </div>
            <TextArea
              label="Any food restrictions or allergies"
              name="foodRestrictions"
              onChange={handleChange}
              rows={3}
            />
          </Section>

          <Section title="Medication (if any)">
            <div className="intake-grid">
              <Field
                label="Medication name"
                name="medName"
                onChange={handleChange}
              />
              <Field label="Dosage" name="medDosage" onChange={handleChange} />
              <Field
                label="Time(s) to give"
                name="medTimes"
                onChange={handleChange}
              />
            </div>
            <TextArea
              label="Special instructions"
              name="medInstructions"
              onChange={handleChange}
              rows={3}
            />
          </Section>

          <Section title="Walks & Exercise">
            <div className="intake-grid">
              <Field
                label="Number of walks per day"
                name="walksPerDay"
                onChange={handleChange}
                type="number"
                min="0"
              />
              <Field
                label="Typical walk length"
                name="walkLength"
                onChange={handleChange}
                placeholder="e.g., 20 minutes"
              />
              <Select
                label="Off-leash allowed?"
                name="offLeashAllowed"
                onChange={handleChange}
                options={[
                  { value: "", label: "Select..." },
                  { value: "Yes", label: "Yes" },
                  { value: "No", label: "No" },
                ]}
              />
            </div>

            <TextArea
              label="Any leash behavior to note (pulling, reactivity, fear, etc.)?"
              name="leashBehavior"
              onChange={handleChange}
              rows={3}
            />
          </Section>

          <Section title="Treats">
            <div className="intake-grid">
              <Field
                label="Approved treats"
                name="approvedTreats"
                onChange={handleChange}
              />
              <Field
                label="Maximum per day"
                name="treatMaxPerDay"
                onChange={handleChange}
              />
            </div>
            <TextArea
              label="Foods to avoid"
              name="foodsToAvoid"
              onChange={handleChange}
              rows={3}
            />
          </Section>

          <Section title="Vet Information">
            <div className="intake-grid">
              <Field
                label="Vet Clinic Name"
                name="vetClinic"
                onChange={handleChange}
              />
              <Field
                label="Phone Number"
                name="vetPhone"
                onChange={handleChange}
                type="tel"
              />
            </div>
            <TextArea
              label="Address"
              name="vetAddress"
              onChange={handleChange}
              rows={2}
            />
          </Section>

          <Section title="Emergency Contacts">
            <div className="intake-grid">
              <Field
                label="Primary Contact Name"
                name="primaryContactName"
                onChange={handleChange}
              />
              <Field
                label="Primary Contact Phone"
                name="primaryContactPhone"
                onChange={handleChange}
                type="tel"
              />
              <Field
                label="Secondary Contact Name"
                name="secondaryContactName"
                onChange={handleChange}
              />
              <Field
                label="Secondary Contact Phone"
                name="secondaryContactPhone"
                onChange={handleChange}
                type="tel"
              />
            </div>
          </Section>

          <Section title="Behavior & Personality">
            <p className="intake-help">
              Please note anything important about your dog:
            </p>

            <div className="intake-checkgrid">
              <Check
                label="Separation anxiety"
                name="separationAnxiety"
                onChange={handleChange}
              />
              <Check
                label="Resource guarding"
                name="resourceGuarding"
                onChange={handleChange}
              />
              <Check
                label="House rules (furniture, sleeping area, crate, etc.)"
                name="houseRules"
                onChange={handleChange}
              />
              <Check
                label="Reactivity"
                name="reactivity"
                onChange={handleChange}
              />
              <Check
                label="Fear of noises, men, bikes, dogs, etc."
                name="fears"
                onChange={handleChange}
              />
            </div>

            <TextArea
              label="Details"
              name="behaviorDetails"
              onChange={handleChange}
              rows={4}
            />
          </Section>

          <Section title="Health & Care Notes">
            <TextArea
              label="Any medical conditions, injuries, sensitivities, or routines we should follow?"
              name="healthNotes"
              onChange={handleChange}
              rows={4}
            />
          </Section>

          <Section title="Additional Notes">
            <TextArea
              label="Anything else you’d like us to know?"
              name="additionalNotes"
              onChange={handleChange}
              rows={4}
            />
          </Section>

          <Section title="Our Care Policy">
            <ul className="intake-list">
              <li>Your dog is covered by our insurance during their stay.</li>
              <li>Dogs are not left alone for more than 2–3 hours per day.</li>
              <li>
                We host a maximum of three dogs at a time, and only if they get
                along well.
              </li>
              <li>
                We prioritize safety, structure, and calm social interaction.
              </li>
            </ul>
          </Section>

          <button className="intake-submit" type="submit">
            Submit Intake Form
          </button>
        </form>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="intake-section">
      <h2 className="intake-h2">{title}</h2>
      {children}
    </section>
  );
}

function Field({
  label,
  name,
  onChange,
  type = "text",
  placeholder,
  required,
  min,
}) {
  return (
    <label className="intake-label">
      <span className="intake-labeltext">
        {label}
        {required ? " *" : ""}
      </span>
      <input
        className="intake-input"
        name={name}
        type={type}
        placeholder={placeholder}
        required={required}
        min={min}
        onChange={onChange}
      />
    </label>
  );
}

function TextArea({ label, name, onChange, rows = 3 }) {
  return (
    <label className="intake-label">
      <span className="intake-labeltext">{label}</span>
      <textarea
        className="intake-textarea"
        name={name}
        rows={rows}
        onChange={onChange}
      />
    </label>
  );
}

function Select({ label, name, onChange, options }) {
  return (
    <label className="intake-label">
      <span className="intake-labeltext">{label}</span>
      <select className="intake-select" name={name} onChange={onChange}>
        {options.map((opt) => (
          <option key={opt.value || opt.label} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function Check({ label, name, onChange }) {
  return (
    <label className="intake-check">
      <input
        className="intake-checkbox"
        type="checkbox"
        name={name}
        onChange={onChange}
      />
      <span className="intake-checktext">{label}</span>
    </label>
  );
}
